import React, { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { BellIcon, WarningIcon, WrenchIcon, ClockIcon, ShieldCheckIcon } from "@phosphor-icons/react";
import api from "@/lib/api";

const CATEGORY_ICON = {
  "Breakdown": WrenchIcon,
  "Delayed Order": ClockIcon,
  "Delivery Risk": ClockIcon,
  "Under-utilised": WarningIcon,
  "Quality Reject": ShieldCheckIcon,
  "Maintenance Due": WrenchIcon,
};

export function useAlerts() {
  const [data, setData] = useState({ count: 0, critical: 0, alerts: [] });
  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get("/dashboard/alerts");
        setData(res.data);
      } catch (e) { /* ignore */ }
    };
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);
  return data;
}

export default function AlertsDrawer({ open, onOpenChange, data }) {
  const alerts = data?.alerts || [];
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="bg-[var(--bg-1)] border-l border-[var(--border)] w-[420px] sm:max-w-[420px] p-0 overflow-y-auto" data-testid="alerts-drawer">
        <SheetHeader className="p-5 border-b border-[var(--border)]">
          <SheetTitle className="font-display flex items-center gap-3">
            <BellIcon size={18} weight="bold" />
            Alerts
            {data?.count > 0 && <span className="text-xs px-2 py-0.5 rounded-sm bg-[var(--accent)]/15 text-[var(--accent)] font-mono">{data.count}</span>}
          </SheetTitle>
          <div className="flex items-center gap-3 text-xs mt-2">
            <span className="text-red-400">{data?.critical || 0} critical</span>
            <span className="text-zinc-500">·</span>
            <span className="text-amber-400">{(data?.count || 0) - (data?.critical || 0)} warnings</span>
          </div>
        </SheetHeader>

        <div className="p-3 space-y-2">
          {alerts.length === 0 && (
            <div className="py-16 text-center text-zinc-500 text-sm">
              <div className="w-12 h-12 rounded-sm bg-emerald-500/10 border border-emerald-500/20 mx-auto flex items-center justify-center mb-3">
                <ShieldCheckIcon size={22} className="text-emerald-400" weight="fill" />
              </div>
              All clear. No active alerts.
            </div>
          )}
          {alerts.map(a => {
            const Icon = CATEGORY_ICON[a.category] || WarningIcon;
            const color = a.severity === "critical" ? "text-red-400 border-red-500/20 bg-red-500/5" :
                          a.severity === "warning" ? "text-amber-400 border-amber-500/20 bg-amber-500/5" :
                          "text-blue-400 border-blue-500/20 bg-blue-500/5";
            return (
              <div key={a.id} className={`p-3 rounded-sm border ${color}`} data-testid={`alert-${a.id}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-sm flex items-center justify-center flex-shrink-0 ${a.severity === "critical" ? "bg-red-500/15" : "bg-amber-500/15"}`}>
                    <Icon size={14} weight="bold" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs overline mb-0.5">{a.category}</div>
                    <div className="text-sm font-semibold text-white truncate">{a.title}</div>
                    <div className="text-xs text-zinc-400 mt-1">{a.detail}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}
